import React, { useContext, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { syncAllPending } from './servicios/offlineSync';
import { AuthProvider, AuthContext } from './context/AuthContext';
import Login from './modulos/login/Login';
import Diseno from './comun/Diseno';
import { puedeVerRuta, rutaInicial, esSuperAdmin } from './comun/permisosRol';
import logo from './assets/logo.png';
import './App.css';

// Cada pantalla se descarga al abrirla (antes todo iba en un solo archivo de ~820 kB).
// Si se despliega una versión nueva con la app abierta, el archivo viejo de la pantalla ya no existe:
// en ese caso se recarga la página una vez para tomar la versión nueva.
const pantalla = (importar) => lazy(() => importar().then((modulo) => {
  try { sessionStorage.removeItem('recarga-por-version'); } catch (e) { /* sin almacenamiento */ }
  return modulo;
}).catch((error) => {
  let yaRecargo = false;
  try { yaRecargo = sessionStorage.getItem('recarga-por-version') === '1'; sessionStorage.setItem('recarga-por-version', '1'); } catch (e) { yaRecargo = true; }
  if (!yaRecargo) {
    window.location.reload();
    return new Promise(() => {});
  }
  throw error;
}));
const ListaUsuarios = pantalla(() => import('./modulos/usuarios/ListaUsuarios'));
const PanelClinica = pantalla(() => import('./modulos/clinica/PanelClinica'));
const Bodega = pantalla(() => import('./modulos/clinica/Bodega'));
const Pacientes = pantalla(() => import('./modulos/clinica/Pacientes'));
const Agenda = pantalla(() => import('./modulos/clinica/Agenda'));
const Tratamientos = pantalla(() => import('./modulos/clinica/Tratamientos'));
const Procedimientos = pantalla(() => import('./modulos/clinica/Procedimientos'));
const Recepcion = pantalla(() => import('./modulos/clinica/Recepcion'));
const HistoriaClinicaVisual = pantalla(() => import('./modulos/clinica/HistoriaClinicaVisual'));
const HistoriaClinicaMSP = pantalla(() => import('./modulos/clinica/HistoriaClinicaMSP'));
const Consultas = pantalla(() => import('./modulos/clinica/Consultas'));
const Reportes = pantalla(() => import('./modulos/clinica/Reportes'));
const RegistroMensual = pantalla(() => import('./modulos/clinica/RegistroMensual'));
const Impresoras = pantalla(() => import('./modulos/clinica/Impresoras'));
const PanelCaja = pantalla(() => import('./modulos/clinica/Caja/PanelCaja'));
const Gastos = pantalla(() => import('./modulos/clinica/Gastos'));
const CieCodes = pantalla(() => import('./modulos/clinica/CieCodes'));
const ClinicalTerms = pantalla(() => import('./modulos/clinica/ClinicalTerms'));
const GraficosConsentimiento = pantalla(() => import('./modulos/clinica/GraficosConsentimiento'));
const Certificados = pantalla(() => import('./modulos/clinica/Certificados'));

const CargandoPantalla = () => <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>Cargando...</div>;

// Componente para proteger rutas
const PrivateRoute = ({ children }) => {
  const { user, loading } = useContext(AuthContext);

  if (loading) {
    return <div>Cargando...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return <Diseno><Suspense fallback={<CargandoPantalla />}>{children}</Suspense></Diseno>;
};

// Además de exigir sesión, bloquea las pantallas que el rol no tiene en su menú
// (antes bastaba con escribir la URL para entrar).
const ClinicRoute = ({ children }) => {
  const { user, loading } = useContext(AuthContext);
  const location = useLocation();

  if (loading) return <div>Cargando...</div>;
  if (!user) return <Navigate to="/login" />;

  if (!puedeVerRuta(user, location.pathname)) {
    const destino = rutaInicial(user);
    if (destino === '/login') {
      return <Diseno><div style={{ padding: '3rem', textAlign: 'center' }}>Tu usuario no tiene un rol con pantallas asignadas. Pide a un administrador que te asigne uno.</div></Diseno>;
    }
    return <Navigate to={destino} replace />;
  }

  return <Diseno><Suspense fallback={<CargandoPantalla />}>{children}</Suspense></Diseno>;
};

// Dashboard simple
const Dashboard = () => {
  const { user } = useContext(AuthContext);
  if (!esSuperAdmin(user)) {
      return <Navigate to="/clinica/agenda" replace />;
  }

  return (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '80vh',
    backgroundColor: '#ffffff',
    color: 'var(--primary-color)',
    textAlign: 'center',
    borderRadius: '16px',
    padding: '3rem',
    margin: '1rem',
    boxShadow: '0 4px 20px rgba(24, 40, 72, 0.1)',
    border: '1px solid var(--border-color)'
  }}>
    <img src={logo} alt="DocRomina" style={{ height: '180px', maxWidth: '100%', marginBottom: '2rem', objectFit: 'contain', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))' }} />
    <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>Bienvenido a DocRomina</h2>
    <p style={{ fontSize: '1.2rem', opacity: 0.9 }}>Seleccione una opción del menú lateral para comenzar a administrar su negocio.</p>
  </div>
  );
};

const App = () => {
    useEffect(() => {
        const handleOnline = () => {
            console.log("Conexión restaurada. Intentando sincronizar datos offline...");
            syncAllPending();
        };

        window.addEventListener('online', handleOnline);
        
        // Intentar sincronizar al cargar la app por primera vez si hay internet
        if (navigator.onLine) {
            syncAllPending();
        }

        return () => window.removeEventListener('online', handleOnline);
    }, []);

    return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />

          <Route path="/" element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          } />

          <Route path="/users" element={
            <ClinicRoute>
              <ListaUsuarios />
            </ClinicRoute>
          } />

          {/* Rutas de la Clínica Médica */}
          <Route path="/clinica" element={
            <ClinicRoute>
              <PanelClinica />
            </ClinicRoute>
          } />
          <Route path="/clinica/pacientes" element={
            <ClinicRoute>
              <Pacientes />
            </ClinicRoute>
          } />
          <Route path="/clinica/certificados" element={
            <ClinicRoute>
              <Certificados />
            </ClinicRoute>
          } />
          <Route path="/clinica/agenda" element={
            <ClinicRoute>
              <Agenda />
            </ClinicRoute>
          } />
          <Route path="/clinica/servicios" element={
            <ClinicRoute>
              <Tratamientos />
            </ClinicRoute>
          } />
          <Route path="/clinica/procedimientos" element={
            <ClinicRoute>
              <Procedimientos />
            </ClinicRoute>
          } />
          <Route path="/clinica/graficos-consentimiento" element={
            <ClinicRoute>
              <GraficosConsentimiento />
            </ClinicRoute>
          } />
          <Route path="/clinica/recepcion" element={
            <ClinicRoute>
              <Recepcion />
            </ClinicRoute>
          } />
          <Route path="/clinica/historia-visual" element={
            <ClinicRoute>
              <HistoriaClinicaVisual />
            </ClinicRoute>
          } />
          <Route path="/clinica/registro-mensual" element={
            <ClinicRoute>
              <RegistroMensual />
            </ClinicRoute>
          } />
          <Route path="/clinica/consultas" element={
            <ClinicRoute>
              <HistoriaClinicaMSP />
            </ClinicRoute>
          } />
          <Route path="/clinica/historia-clinica" element={
            <ClinicRoute>
              <HistoriaClinicaMSP />
            </ClinicRoute>
          } />
          <Route path="/clinica/presupuestos-recetas" element={
            <ClinicRoute>
              <Consultas />
            </ClinicRoute>
          } />
          <Route path="/clinica/bodega" element={
            <ClinicRoute>
              <Bodega />
            </ClinicRoute>
          } />

          <Route path="/clinica/shift" element={
            <ClinicRoute>
              <PanelCaja />
            </ClinicRoute>
          } />
          <Route path="/clinica/gastos" element={
            <ClinicRoute>
              <Gastos />
            </ClinicRoute>
          } />
          <Route path="/clinica/reports" element={
            <ClinicRoute>
              <Reportes />
            </ClinicRoute>
          } />
          <Route path="/clinica/printers" element={
            <ClinicRoute>
              <Impresoras />
            </ClinicRoute>
          } />
          <Route path="/clinica/cie-codes" element={
            <ClinicRoute>
              <CieCodes />
            </ClinicRoute>
          } />
          <Route path="/clinica/clinical-terms" element={
            <ClinicRoute>
              <ClinicalTerms />
            </ClinicRoute>
          } />

          {/* Redirigir cualquier otra ruta al inicio */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
